import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Building2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const rentalStatuses = ["active", "ended", "pending"];

const emptyForm = {
  property_id: "",
  unit_id: "",
  tenant_name: "",
  tenant_email: "",
  tenant_phone: "",
  start_date: "",
  end_date: "",
  monthly_rent: "",
  basic_rent: "",
  service_charges: "",
  deposit: "",
  max_occupants: "",
  status: "active",
  notes: "",
};

export default function Rentals() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [rentals, setRentals] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRental, setEditingRental] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");

  const canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  const isAdmin = user?.role === "admin";

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [rentalsRes, propertiesRes, unitsRes] = await Promise.all([
        fetch(`${API_URL}/api/rentals`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} }),
        fetch(`${API_URL}/api/properties`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} }),
        fetch(`${API_URL}/api/units`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} }),
      ]);
      if (rentalsRes.ok) setRentals(await rentalsRes.json());
      if (propertiesRes.ok) setProperties(await propertiesRes.json());
      if (unitsRes.ok) setUnits(await unitsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  function getFilteredUnits() {
    var result = [];
    for (var i = 0; i < units.length; i++) {
      var u = units[i];
      if (!form.property_id || form.property_id === "none" || u.property_id === form.property_id) {
        result.push(u);
      }
    }
    return result;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that Monthly Rent = Basic Rent + Service Charges
    const monthlyRent = parseFloat(form.monthly_rent) || 0;
    const basicRent = parseFloat(form.basic_rent) || 0;
    const serviceCharges = parseFloat(form.service_charges) || 0;
    
    // Check if basic_rent and service_charges are provided
    if (form.basic_rent && form.service_charges) {
      const calculatedTotal = basicRent + serviceCharges;
      // Allow small floating point differences (0.01)
      if (Math.abs(monthlyRent - calculatedTotal) > 0.01) {
        toast.error(
          t("rentals.rentValidationError", {
            monthlyRent: monthlyRent.toFixed(2),
            basicRent: basicRent.toFixed(2),
            serviceCharges: serviceCharges.toFixed(2),
            calculatedTotal: calculatedTotal.toFixed(2)
          }),
          { duration: 6000 }
        );
        return;
      }
    }
    
    try {
      const url = editingRental ? `${API_URL}/api/rentals/${editingRental.rental_id}` : `${API_URL}/api/rentals`;
      const method = editingRental ? "PUT" : "POST";

      const payload = {
        ...form,
        property_id: form.property_id === "none" ? null : form.property_id,
        unit_id: form.unit_id === "none" ? null : form.unit_id || null,
        monthly_rent: parseFloat(form.monthly_rent),
        basic_rent: form.basic_rent ? parseFloat(form.basic_rent) : null,
        service_charges: form.service_charges ? parseFloat(form.service_charges) : null,
        deposit: form.deposit ? parseFloat(form.deposit) : null,
        max_occupants: form.max_occupants ? parseInt(form.max_occupants) : null,
      };

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingRental ? "Rental updated" : "Rental created");
        setDialogOpen(false);
        setEditingRental(null);
        setForm(emptyForm);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Operation failed");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleEdit = (rental) => {
    setEditingRental(rental);
    setForm({
      property_id: rental.property_id || "",
      unit_id: rental.unit_id || "",
      tenant_name: rental.tenant_name || "",
      tenant_email: rental.tenant_email || "",
      tenant_phone: rental.tenant_phone || "",
      start_date: rental.start_date || "",
      end_date: rental.end_date || "",
      monthly_rent: rental.monthly_rent?.toString() || "",
      basic_rent: rental.basic_rent?.toString() || "",
      service_charges: rental.service_charges?.toString() || "",
      deposit: rental.deposit?.toString() || "",
      max_occupants: rental.max_occupants?.toString() || "",
      status: rental.status || "active",
      notes: rental.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (rentalId) => {
    if (!confirm("Are you sure you want to delete this rental?")) return;
    try {
      const response = await fetch(`${API_URL}/api/rentals/${rentalId}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        toast.success("Rental deleted");
        fetchData();
      } else {
        toast.error("Failed to delete rental");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find((p) => p.property_id === propertyId);
    return prop?.name || "-";
  };

  const getUnitName = (unitId) => {
    if (!unitId) return null;
    const unit = units.find((u) => u.unit_id === unitId);
    return unit?.name || null;
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: "status-active",
      ended: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
      pending: "status-pending",
    };
    return <Badge className={statusClasses[status] || ""}>{t(`rentals.statuses.${status}`)}</Badge>;
  };

  const filteredRentals = rentals.filter(
    (r) => r.tenant_name.toLowerCase().includes(search.toLowerCase()) || getPropertyName(r.property_id).toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rentals-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("rentals.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("rentals.subtitle")}</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingRental(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-rental-btn"><Plus className="w-4 h-4 mr-2" />{t("rentals.addRental")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingRental ? t("rentals.editRental") : t("rentals.addNewRental")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="form-label">{t("assets.property")}</Label>
                  <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v, unit_id: "" })}>
                    <SelectTrigger data-testid="rental-property-select"><SelectValue placeholder={t("assets.property")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-</SelectItem>
                      {properties.map((p) => (<SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {getFilteredUnits().length > 0 && (
                  <div>
                    <Label className="form-label">{t("nav.units")} ({t("common.optional")})</Label>
                    <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                      <SelectTrigger data-testid="rental-unit-select"><SelectValue placeholder={t("nav.units")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">- {t("properties.rentalTypeWhole")}</SelectItem>
                        {getFilteredUnits().map((u) => (<SelectItem key={u.unit_id} value={u.unit_id}>{u.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="form-label">{t("rentals.tenantName")}</Label>
                  <Input value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} placeholder="Tenant full name" required data-testid="rental-tenant-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("rentals.tenantEmail")}</Label>
                    <Input type="email" value={form.tenant_email} onChange={(e) => setForm({ ...form, tenant_email: e.target.value })} placeholder="email@example.com" data-testid="rental-tenant-email-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("rentals.tenantPhone")}</Label>
                    <Input value={form.tenant_phone} onChange={(e) => setForm({ ...form, tenant_phone: e.target.value })} placeholder="+1..." data-testid="rental-tenant-phone-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("contracts.startDate")}</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required data-testid="rental-start-date-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("contracts.endDate")}</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="rental-end-date-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("rentals.monthlyRent")}</Label>
                    <Input type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} placeholder="0" required data-testid="rental-monthly-rent-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("rentals.deposit")}</Label>
                    <Input type="number" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} placeholder="0" data-testid="rental-deposit-input" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="form-label">{t("rentals.basicRent")}</Label>
                    <Input type="number" value={form.basic_rent} onChange={(e) => setForm({ ...form, basic_rent: e.target.value })} placeholder="0" data-testid="rental-basic-rent-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("rentals.serviceCharges")}</Label>
                    <Input type="number" value={form.service_charges} onChange={(e) => setForm({ ...form, service_charges: e.target.value })} placeholder="0" data-testid="rental-service-charges-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("rentals.maxOccupants")}</Label>
                    <Input type="number" value={form.max_occupants} onChange={(e) => setForm({ ...form, max_occupants: e.target.value })} min="1" placeholder="1" data-testid="rental-max-occupants-input" />
                  </div>
                </div>
                <div>
                  <Label className="form-label">{t("common.status")}</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="rental-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {rentalStatuses.map((s) => (<SelectItem key={s} value={s}>{t(`rentals.statuses.${s}`)}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">{t("common.notes")}</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t("common.notes")} rows={2} data-testid="rental-notes-input" />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" data-testid="rental-submit-btn">{editingRental ? t("common.update") : t("common.create")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder={t("common.search")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="rental-search-input" />
      </div>

      {filteredRentals.length > 0 ? (
        <Card className="bento-card overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("assets.property")}</TableHead>
                <TableHead>{t("nav.units")}</TableHead>
                <TableHead>{t("rentals.tenantName")}</TableHead>
                <TableHead>{t("contracts.startDate")}</TableHead>
                <TableHead>{t("contracts.endDate")}</TableHead>
                <TableHead>{t("rentals.monthlyRent")}</TableHead>
                <TableHead>{t("rentals.maxOccupants")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                {canEdit && <TableHead className="w-20">{t("common.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRentals.map(function(rental) {
                var rentalId = rental.rental_id;
                var rentalData = JSON.parse(JSON.stringify(rental));
                return (
                <TableRow key={rentalId} data-testid={"rental-row-" + rentalId}>
                  <TableCell className="font-medium">{getPropertyName(rental.property_id)}</TableCell>
                  <TableCell>{getUnitName(rental.unit_id) || "-"}</TableCell>
                  <TableCell>{rental.tenant_name}</TableCell>
                  <TableCell>{rental.start_date}</TableCell>
                  <TableCell>{rental.end_date || "-"}</TableCell>
                  <TableCell>{formatPrice(rental.monthly_rent)}</TableCell>
                  <TableCell>{rental.max_occupants || "-"}</TableCell>
                  <TableCell>{getStatusBadge(rental.status)}</TableCell>
                  {canEdit && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={handleEdit.bind(null, rentalData)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={handleDelete.bind(null, rentalId)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("rentals.noRentals")}</p>
            {canEdit && <Button className="mt-4" onClick={() => setDialogOpen(true)}>{t("rentals.addFirstRental")}</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
